// #1780 — the blocker codes the HTS badge names, the sentence it says, and the
// one line the popup composes rather than renders.
//
// PURE, and separate from the mixin that fetches, for two reasons. It is shared
// by both eyeball surfaces (single + bulk), and a copy in each is exactly the
// duplication that let #1871 fix one of two identical query sites and miss the
// other. And it is the one piece with a safety property worth testing directly:
// these strings are interpolated into a notification.
//
//   node callsteward-web/tests/hts-blockers-1780.test.mjs

/**
 * The complete set. RU and vacation are HARD (nothing a dispatcher can do about
 * them); a booking sometimes is not, which is why it sorts last.
 *
 * ⚠ The SERVER decides which of these apply, from the same booleans that decided
 * the verdict. Nothing here re-derives them: a frontend guessing which badge to
 * show is the original defect — #1661 records it rendering a BOOKING as
 * "vacation", which is how a dispatcher came to distrust the badge in the first
 * place.
 */
export const ALLOWED_BLOCKERS = {
    RU: 'Recurring unavailability',
    V: 'Vacation',
    Book: 'Booked',
};

/**
 * Server codes, passed through the set above.
 *
 * The filter is not distrust of our own API. It is that the label lands inside a
 * user-visible notification, and a fixed vocabulary is the one shape that cannot
 * become an injection the day something else populates that field — so the label
 * shown is ALWAYS ours, never the server's string.
 */
export function blockerList(raw) {
    if (!Array.isArray(raw)) return [];

    return raw
        .filter((b) => b && ALLOWED_BLOCKERS[b.code])
        .map((b) => ({ code: b.code, label: ALLOWED_BLOCKERS[b.code] }));
}

/**
 * The blocked notification's sentence. Names the blockers, and says the detail
 * is a click away — the missing half of the old message, which stated a verdict
 * and gave no route to the facts behind it.
 */
export function blockedMessage(serverMessage, blockers) {
    const base = serverMessage || 'Not available for HTS';
    if (!blockers || !blockers.length) return `${base} — click for details.`;

    return `${base} — ${blockers.map((b) => b.label).join(', ')}. Click for details.`;
}

/**
 * The line an EXTERNAL (cross-tenant) booking shows.
 *
 * ed, 2026-09-10: the popup names the other local, as the pill does under #1821
 * — and nothing else. No job, no times, no call detail; those never leave the
 * backend, so there is nothing here to hide.
 *
 * Composed here rather than shipped whole, for the same reason `blockerList`
 * filters: the FIXED WORDS stay ours and only the name is data. And #1821 is
 * explicit that a client row with no name must read as a plain "Other booking",
 * never a dangling "Other booking — ", which is a property worth a test rather
 * than a template expression nobody looks at twice.
 */
export function externalBookingLabel(booking) {
    const base = (booking && booking.label) || 'Other booking';
    const name = booking && booking.client_name ? String(booking.client_name).trim() : '';

    return name ? `${base} — ${name}` : base;
}
